import { faroCoreApiClient } from "@api/core-api/core-api-utils";
import { PageSubtitle } from "@components/common/page-subtitle";
import { PageTitle } from "@components/common/page-title";
import { UlmInputLabel } from "@components/ulm/ulm-input-label";
import { UlmButton } from "@components/ulm/ulm-button";
import { UlmContainer } from "@components/ulm/ulm-container";
import { UlmTextField } from "@components/ulm/ulm-text-field";
import { useErrorHandlerContext } from "@context-providers/error-handler/error-handler-context";
import { useToast } from "@flat-ui";
import { Stack } from "@mui-wrapper";
import { EmailUtils } from "@stellar/utils";
import { useCallback, useEffect, useMemo, useState } from "react";

/** Renders component to resend a verification email */
export function ResendVerificationEmail(): JSX.Element {
  const { openToast } = useToast();
  const { handleError } = useErrorHandlerContext();

  const [description, setDescription] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [isEmailInputError, setIsEmailInputError] = useState<boolean>(false);
  const [isResendingEmail, setIsResendingEmail] = useState<boolean>(false);
  const [shouldShowInputAndButton, setShouldShowInputAndButton] =
    useState<boolean>(true);

  const sanitizedEmail = useMemo(() => {
    return email.trim();
  }, [email]);

  const isValidEmailFormat = useMemo(() => {
    return EmailUtils.isValidEmail(sanitizedEmail);
  }, [sanitizedEmail]);

  const resendEmail = useCallback(async () => {
    if (!isValidEmailFormat) {
      setIsEmailInputError(true);
      return;
    }

    setIsResendingEmail(true);

    try {
      await faroCoreApiClient.users.resendVerificationMail(sanitizedEmail);
      setShouldShowInputAndButton(false);

      setDescription(
        "Verification email successfully sent. Please check your email inbox for confirmation."
      );

      openToast({
        title: "Verification email successfully sent.",
        variant: "success",
      });
    } catch (error) {
      handleError({
        id: `resendVerificationEmail-${Date.now().toString()}`,
        title: "Failed to resend a new verification email.",
        error,
        uiType: "toast",
      });
    }

    setIsResendingEmail(false);
  }, [handleError, isValidEmailFormat, openToast, sanitizedEmail]);

  const onEmailKeyUp = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      event.stopPropagation();
      if (event.key === "Enter") {
        void resendEmail();
      }
    },
    [resendEmail]
  );

  /** Resets the email input error state if the input it's cleared or if it has a valid email format */
  useEffect(() => {
    if (sanitizedEmail === "" || isValidEmailFormat) {
      setIsEmailInputError(false);
    }
  }, [isValidEmailFormat, sanitizedEmail]);

  return (
    <UlmContainer>
      <PageTitle>Resend Verification Email</PageTitle>
      <PageSubtitle>{description}</PageSubtitle>

      {shouldShowInputAndButton && (
        <>
          <Stack
            sx={{
              marginTop: "20px",
              width: "100%",
            }}
          >
            <UlmInputLabel title="Email" />
            <UlmTextField
              value={email}
              type="email"
              name="email"
              autoComplete="email"
              isValidValue={isValidEmailFormat}
              isError={isEmailInputError}
              errorText={"Enter a valid email address"}
              placeholder={"Enter your email"}
              onChange={(event) => setEmail(event.target.value)}
              onKeyUp={onEmailKeyUp}
              isDisabled={isResendingEmail}
            />
          </Stack>

          <UlmButton
            disabled={sanitizedEmail === ""}
            isLoading={isResendingEmail}
            isLoadingText="Resending..."
            onClick={resendEmail}
            sx={{
              marginTop: "20px",
            }}
          >
            Resend Email
          </UlmButton>
        </>
      )}
    </UlmContainer>
  );
}
